import LikeOutline from '@simplywallst/ui-core/icons/LikeOutline';
import CommentOutline from '@simplywallst/ui-core/icons/CommentOutline';
import UserProfileOutline from '@simplywallst/ui-core/icons/UserProfileOutline';
import { toCurrencyTruncated } from '@/utilities/formatting';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import { ROUTE_NARRATIVE_COMMUNITY_DETAIL } from '@/constants/routes';
import { Badge, Tooltip, Heading, cn } from '@simplywallst/ui-core/next';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { getCompanyUrlSegments } from '@/pages/Narrative/utils/getNarrativeUrl';
import { RouterLinkOptimised } from '../../../Narratives/components/RouterLinkOptimised';
import { NarrativeCardTimeBadge } from './components/NarrativeCardTimeBadge';
import { useUserProfile } from '@/hooks/useUserProfile';
import { NarrativeAuthorAvatar } from '@/components/NarrativeAuthor';
import { CompanyIntrinsicDiscountBase } from '@/components/CompanyIntrinsicDiscount';
import type { HeadingElements } from '@/utilities/heading';
import type { FragmentOf } from '@/utilities/gql';
import type { NARRATIVE_CARD_FRAGMENT } from '../../fragments';

export const NarrativeCard = ({
  narrative,
  onClick,
  className,
  titleAs = 'h3',
}: {
  onClick?: () => void;
  className?: string;
  titleAs?: HeadingElements;
  narrative: FragmentOf<typeof NARRATIVE_CARD_FRAGMENT>;
}) => {
  const {
    company,
    primaryCount,
    slug,
    updatedAt,
    owner,
    valuation,
    voteCount,
    isPublic,
    commentCount,
    isSponsored,
    draftUpdate,
    earliestPublishedUpdate,
    latestPublishedUpdate,
    title,
  } = narrative;

  const { data, name } = company ?? {};
  const userProfile = useUserProfile();
  const isOwner = userProfile?.publicId === owner.publicId;
  const isDraft = isOwner && !earliestPublishedUpdate?.willLockAt;

  const formattedFairValue = toCurrencyTruncated(
    draftUpdate?.valuation?.fairValue || valuation?.fairValue || 0,
    2,
    getTradingItemCurrencyISO(data?.currencyInfo) ?? 'XXX'
  );

  const titleNode = (
    <Heading
      as={titleAs}
      className={cn(
        'm-0 line-clamp-2 text-sm font-strong text-solid',
        isPublic &&
          'group-focus-within/narrative-link:underline group-hover/narrative-link:underline'
      )}
    >
      {draftUpdate?.title ||
        latestPublishedUpdate?.title ||
        title ||
        `I believe ${name}'s Fair Value is ${formattedFairValue}`}
    </Heading>
  );

  const { country, industry, exchangeTicker, companyName } =
    getCompanyUrlSegments(company?.canonicalURL ?? '');

  return (
    <div className={cn('grid h-full gap-2', className)}>
      <div className="grid grid-cols-[auto_1fr] gap-2.5">
        <div
          // Fix: Unsure why but sometimes the 'AnalystConsensusTarget' avatar doesn't display at 100% width
          className="[&_img]:w-full"
        >
          <NarrativeAuthorAvatar
            size="md"
            owner={{
              displayName: owner.displayName ?? 'User',
              imageUrl: owner.imageUrl,
            }}
            priority={false}
          />
        </div>
        <div className="grid gap-1.5">
          <div className="grid grid-flow-col items-center justify-start gap-1 truncate leading-7 text-soft">
            <RouterLinkOptimised
              className="truncate text-[inherit] no-underline hover:underline focus:underline"
              to={`/community/users/${owner.publicId}`}
            >
              {owner.displayName ?? 'User'}
            </RouterLinkOptimised>
            <span className="text-2xl leading-[0] text-softer">&middot;</span>
            {isDraft ? (
              <Badge tone="warning" size="small">
                Draft
              </Badge>
            ) : (
              <NarrativeCardTimeBadge
                narrativeWillLockAt={earliestPublishedUpdate?.willLockAt}
                updatedAt={draftUpdate?.updatedAt ?? updatedAt}
                updateWillLockAt={latestPublishedUpdate?.willLockAt}
                lastFeaturedAt={narrative.lastFeaturedAt}
              />
            )}
          </div>

          <div className="truncate text-xs">
            <span className="">Fair Value</span> {formattedFairValue}{' '}
            <span className="ml-1 [&>*]:inline">
              <CompanyIntrinsicDiscountBase
                discount={
                  (draftUpdate?.valuation?.intrinsicDiscount ||
                    valuation?.intrinsicDiscount) ??
                  null
                }
              />
            </span>
          </div>

          {isPublic ? (
            <RouterLinkOptimised
              className="group/narrative-link no-underline decoration-p-white"
              to={{
                type: ROUTE_NARRATIVE_COMMUNITY_DETAIL,
                payload: {
                  country,
                  exchange_ticker: exchangeTicker,
                  industry,
                  companyname: companyName,
                  slug,
                },
              }}
              onClick={onClick}
            >
              {titleNode}
            </RouterLinkOptimised>
          ) : (
            <div className="cursor-default">{titleNode}</div>
          )}

          <div className="grid grid-flow-col items-center justify-between gap-2">
            <div className="grid grid-flow-col items-center justify-start gap-2 fill-softer text-xs text-softer">
              <div className="grid grid-cols-[min-content_min-content] items-center gap-x0_5">
                <LikeOutline className="size-x2" />
                <div>
                  {voteCount.upvotes}
                  <span className="sr-only">
                    users have liked this narrative
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-[min-content_min-content] items-center gap-x0_5">
                <CommentOutline className="size-x2" />
                <div>
                  {commentCount}
                  <span className="sr-only">
                    users have commented on this narrative
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-[min-content_min-content] items-center gap-x0_5">
                <UserProfileOutline className="size-x2" />
                <div>
                  {primaryCount}
                  <span className="sr-only">
                    users have followed this narrative
                  </span>
                </div>
              </div>
            </div>

            {isSponsored && (
              <Tooltip
                placement="top"
                content={
                  <div className="max-w-x20 text-pretty text-tiny text-solid">
                    {`This Narrative is sponsored by ${company?.name}. Simply Wall St received a fee to promote it. The views are those of the Sponsor and do not reflect Simply Wall St's opinions.`}
                  </div>
                }
              >
                <button className="cursor-default">
                  <Badge
                    className="grid grid-cols-[min-content_max-content] items-center justify-start gap-x0_25 bg-[--s-button-secondary-color-bg]"
                    size="small"
                  >
                    <InformationSolid className="size-3 fill-chart-01" />
                    <span className="text-chart-01">Sponsored</span>
                  </Badge>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
